// File: components/WorkEnvironment.js
import React, { useState } from 'react';
import SummaryBox from './SummaryBox';

const WorkEnvironment = ({
  workEnvironment,
  setWorkEnvironment,
  workEnvironments, // This is now passed from the parent component based on location
  environmentInfo,
  location,
  applyLocationMultiplier,
  formatCurrency
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const selectedEnv = workEnvironments.find(env => env.name === workEnvironment);

  // Helper to get the name part from the description
  const getEnvironmentName = (description) => {
    if (!description) return '';
    const parts = description.split(':');
    return parts.length > 0 ? parts[0].trim() : description;
  };

  // Calculate monthly cost with location adjustment
  const getMonthlyRate = (annualCost) => {
    // No need to apply multiplier here as costs are already location-specific
    return annualCost / 12;
  };

  // Helper to display costs
  const displayCost = (cost) => {
    if (cost === 0) return environmentInfo.freeLabel;
    return formatCurrency(cost);
  };

  // Prepare summary items
  const getSummaryItems = () => {
    const items = [];

    // Add cost information
    if (selectedEnv.cost === 0) {
      items.push({
        label: environmentInfo.costLabel,
        value: environmentInfo.freeLabel,
        type: "neutral",
        bold: true
      });
    } else {
      items.push({
        label: environmentInfo.monthlyCostLabel,
        value: formatCurrency(getMonthlyRate(selectedEnv.cost)),
        type: "cost",
        bold: true
      });
      items.push({
        label: environmentInfo.annualCostLabel,
        value: formatCurrency(selectedEnv.cost),
        type: "cost",
        bold: true
      });
    }

    // Add success modifier
    items.push({
      label: environmentInfo.successModifierLabel,
      value: selectedEnv.modifier > 0 ? `+${selectedEnv.modifier}%` : `${selectedEnv.modifier}%`,
      type: "success",
      bold: true
    });

    return items;
  };

  return (
    <div className="mb-6">
      <div className="flex items-center mb-1">
        <h2 className="text-xl font-semibold">{environmentInfo.title}</h2>
        <button
          className="ml-2 text-blue-500 text-sm"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? environmentInfo.hideDetailsLabel : environmentInfo.showDetailsLabel}
        </button>
      </div>

      {showDetails && (
        <div className="bg-blue-50 p-3 rounded mb-3 text-sm">
          <p className="mb-2">
            {environmentInfo.environmentDescriptionLabel}
          </p>
          <ul className="list-disc pl-5">
            {workEnvironments.map((env) => (
              <li key={env.name} className={workEnvironment === env.name ? "font-semibold" : ""}>
                <strong>{getEnvironmentName(env.description)}</strong>
                <div className="mt-1">{env.detailedDescription}</div>
                <div className="mt-1">
                  <strong>{environmentInfo.annualCostLabel2}</strong>{' '}
                  {env.cost === 0 ? environmentInfo.freeLabel : `${formatCurrency(env.cost)}${environmentInfo.perYearLabel}`}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <select
        value={workEnvironment}
        onChange={(e) => setWorkEnvironment(e.target.value)}
        className="w-full p-2 border rounded"
      >
        {workEnvironments.map((env) => (
          <option key={env.name} value={env.name}>
            {env.description}
          </option>
        ))}
      </select>

      {selectedEnv && (
        <div className="mt-3">
          <SummaryBox
            title={`${getEnvironmentName(selectedEnv.description)}`}
            description={selectedEnv.detailedDescription}
            items={getSummaryItems()}
          />
        </div>
      )}
    </div>
  );
};

export default WorkEnvironment;